/**
 * aiTaskLaunchStore — per-tab queue of AI task prompts waiting to be sent.
 *
 * `openAiChatWithTask` can be called from surfaces that have no access to a
 * tab's chat session (Object Explorer, Schema Overview, compare results), and
 * can target a tab that has not mounted yet. So the prompt is parked here and
 * the tab drains it (`useAiTaskLaunchDrain`) once it is mounted and idle.
 *
 * The queue is deliberately a plain store rather than an event: a zustand
 * snapshot read at mount sees enqueues that happened before the tab existed,
 * which an event would have dropped.
 *
 * Shape mirrors `terminal/store/aiExecutionStore.ts` — same seam, same lifetime
 * rules — except entries are a FIFO per tab rather than one pending request,
 * because a user can fire a second task while the first run is still going.
 */

import { create } from 'zustand';
import type { AiTaskId } from '../launchers/launcherRegistry';

export type AiTaskLaunchEntry = {
  id: string;
  taskId: AiTaskId;
  /** The full prompt, already built by the launcher. */
  prompt: string;
  queuedAt: number;
};

type AiTaskLaunchState = {
  /** tabId -> queued prompts, oldest first. */
  queues: Record<string, AiTaskLaunchEntry[]>;

  /** Queue a prompt for a tab; returns the entry id used to dequeue it. */
  enqueue: (tabId: string, entry: { taskId: AiTaskId; prompt: string }) => string;

  /** The next prompt for a tab, without removing it. */
  peek: (tabId: string) => AiTaskLaunchEntry | undefined;

  /** Remove one entry (called the moment it is handed to `sendMessage`). */
  dequeue: (tabId: string, entryId: string) => void;

  /** Drop everything queued for a tab — it closed, or it never mounted. */
  clearTab: (tabId: string) => void;

  /** Drop queues whose tab no longer exists, so entries cannot leak. */
  pruneQueues: (existingTabIds: string[]) => void;
};

let entrySeq = 0;

export const useAiTaskLaunchStore = create<AiTaskLaunchState>((set, get) => ({
  queues: {},

  enqueue: (tabId, entry) => {
    entrySeq += 1;
    const id = `ai-task-${Date.now()}-${entrySeq}`;
    set((state) => ({
      queues: {
        ...state.queues,
        [tabId]: [
          ...(state.queues[tabId] ?? []),
          { id, taskId: entry.taskId, prompt: entry.prompt, queuedAt: Date.now() },
        ],
      },
    }));
    return id;
  },

  peek: (tabId) => get().queues[tabId]?.[0],

  dequeue: (tabId, entryId) => {
    set((state) => {
      const queue = state.queues[tabId];
      if (!queue) return state;
      const remaining = queue.filter((entry) => entry.id !== entryId);
      if (remaining.length === queue.length) return state;
      const { [tabId]: _removed, ...rest } = state.queues;
      void _removed;
      return { queues: remaining.length ? { ...rest, [tabId]: remaining } : rest };
    });
  },

  clearTab: (tabId) => {
    set((state) => {
      if (!state.queues[tabId]) return state;
      const { [tabId]: _removed, ...rest } = state.queues;
      void _removed;
      return { queues: rest };
    });
  },

  pruneQueues: (existingTabIds) => {
    set((state) => {
      const alive = new Set(existingTabIds);
      const kept = Object.entries(state.queues).filter(([tabId]) => alive.has(tabId));
      if (kept.length === Object.keys(state.queues).length) return state;
      return { queues: Object.fromEntries(kept) };
    });
  },
}));
